import { createTransaction } from './transaction';
import { arrayBufferToBase64, createQueryParams } from './utils';
import { useConfigStore } from '~app/core/config/store';
import {
  AuthUrlOptions,
  CodeChallenge,
  LogoutOptions,
  OIDCTokenModel,
  OAuthTokenOptions,
  RedirectLoginOptions,
} from '../types';
import axios from 'axios';
import { deserialize } from '~app/shared/json-mapper';

function _buildAuthUrl({
  codeChallenge: code_challenge,
  codeChallengeMethod: code_challenge_method,
  responseType: response_type = 'code',
  state,
}: AuthUrlOptions): string {
  const {
    clientId: client_id,
    host,
    redirectUri: redirect_uri,
    resource,
    scope,
    claims,
  } = useConfigStore().sso;

  return `${host}/auth?${createQueryParams({
    client_id,
    redirect_uri,
    resource,
    scope,
    claims,
    code_challenge,
    code_challenge_method,
    state,
    response_type,
  })}`;
}

function _buildLogoutUrl({ state }: LogoutOptions = {}): string {
  const {
    clientId: client_id,
    host,
    postLogoutRedirectUri: post_logout_redirect_uri,
  } = useConfigStore().sso;

  return `${host}/session/end?${createQueryParams({
    client_id,
    post_logout_redirect_uri,
    state,
  })}`;
}

function _generateCodeChallenge(codeVerifier: string): Promise<CodeChallenge> {
  return new Promise<CodeChallenge>((resolve) => {
    if (typeof TextEncoder === 'undefined' || typeof crypto === 'undefined') {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);

      return crypto.subtle
        .digest('SHA-256', data)
        .then((res) =>
          arrayBufferToBase64(res).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''),
        )
        .then((codeChallenge) => ({
          codeChallenge,
          codeChallengeMethod: 'S256',
        }));
    }

    return resolve({
      codeChallenge: codeVerifier,
      codeChallengeMethod: 'plain',
    });
  });
}

function _generateCodeVerifier(len = 128): string {
  const scope = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let code = '';

  for (let i = 0; i < len; i++) {
    code += scope.charAt(Math.floor(Math.random() * scope.length));
  }

  return code;
}

function _redirectToAuthPage(options: AuthUrlOptions) {
  window.location.href = _buildAuthUrl(options);
}

function _redirectToLogoutPage(options: LogoutOptions = {}) {
  window.location.href = _buildLogoutUrl(options);
}

export async function loginWithRedirect(options: RedirectLoginOptions = {}): Promise<void> {
  const { loginHint, prompt, targetUrl } = options;
  const codeVerifier = _generateCodeVerifier();
  const codeChallengePromise = _generateCodeChallenge(codeVerifier);

  return codeChallengePromise
    .then(({ codeChallenge, codeChallengeMethod }): AuthUrlOptions => {
      const { id: state } = createTransaction({
        codeVerifier,
        targetUrl,
      });

      return {
        state,
        codeChallenge,
        codeChallengeMethod,
        loginHint,
        prompt,
      };
    })
    .then(_redirectToAuthPage)
    .then(() => new Promise(() => void 0));
}

export function logout(options?: LogoutOptions): Promise<void> {
  _redirectToLogoutPage(options);
  return new Promise(() => void 0);
}

export function getOAuthToken({
  code,
  codeVerifier: code_verifier,
  grantType: grant_type,
  refreshToken: refresh_token,
}: OAuthTokenOptions): Promise<OIDCTokenModel> {
  const {
    host: baseURL,
    clientId: client_id,
    redirectUri: redirect_uri,
    resource,
  } = useConfigStore().sso;

  return axios({
    method: 'post',
    baseURL,
    url: '/token',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    data: createQueryParams({
      client_id,
      redirect_uri,
      code,
      code_verifier,
      grant_type,
      refresh_token,
      resource,
    }),
  }).then((res) => deserialize(OIDCTokenModel, res.data));
}

export * from '../types';
